import { Fragment, useEffect, useState } from 'react';
import Widget from 'components/Dashboard/Widget';
import Select from 'components/inputs/Select';
import { useKPIGetDeclinedJobs } from 'api/kpi/kpi';
import { DeclinedJobsCountyDimension } from 'api/model';
import { KPIsWidgetEnum, LongTitles } from '../../utils';
import useOperationsKpiFilter from './useOperationsKpiFilter';
import useExpandedRows from './useExpandedRows';
import localityLabel from './locality';
import periodLabel from './periodLabel';
import {
  nf,
  pct,
  tableStyle,
  tdNumStyle,
  tdStyle,
  thNumStyle,
  thStyle,
  totalRowStyle,
  WidgetState,
} from '../management/shared';

const DIMENSION_LABELS: Record<DeclinedJobsCountyDimension, string> = {
  [DeclinedJobsCountyDimension.HomeCounty]: 'Fälttestarens hemlän',
  [DeclinedJobsCountyDimension.FromCounty]: 'Uppdragets från-län',
  [DeclinedJobsCountyDimension.ToCounty]: 'Uppdragets till-län',
};

/**
 * Nekade jobb av besvarade tilldelningar, per period och län. Länet finns i tre betydelser —
 * var fälttestaren bor, var uppdraget börjar och var det slutar — och alla tre summerar till
 * samma total.
 */
const DeclinedJobsWidget = () => {
  const { isReady, isDateRangeTooLong, groupingLevel, filter, deps } =
    useOperationsKpiFilter();
  const getData = useKPIGetDeclinedJobs();
  const [dimension, setDimension] = useState<DeclinedJobsCountyDimension>(
    DeclinedJobsCountyDimension.HomeCounty
  );

  useEffect(() => {
    if (isReady) {
      getData.mutate({ data: filter });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  const expanded = useExpandedRows();
  const result = getData.data;
  const counties = (result?.byCounty ?? []).filter(
    (r) => r.dimension === dimension
  );
  // Från-länet jämförs med FT:s hemankare: nekar FT jobb i det egna länet eller längre bort?
  const canExpand = dimension === DeclinedJobsCountyDimension.FromCounty;

  return (
    <Widget
      title={LongTitles[KPIsWidgetEnum.DeclinedJobs]}
      count={result?.declined}
      isLoading={getData.isLoading}
      info="Uppdrag som fälttestaren nekat i appen, av besvarade tilldelningar (nekade + accepterade). Dateras på när svaret gavs. Per från-län: klicka på ett län för att dela upp på om fälttestaren har hemankare i uppdragets från-län."
    >
      <WidgetState
        isLoading={getData.isLoading}
        isDateRangeTooLong={isDateRangeTooLong}
        isEmpty={!result || result.declined + result.accepted === 0}
      >
        <p style={{ fontSize: '15px', margin: '4px 0 12px' }}>
          <strong>{pct(result?.declinedPercentage)}</strong> nekade ·{' '}
          {nf(result?.declined ?? 0)} av{' '}
          {nf((result?.declined ?? 0) + (result?.accepted ?? 0))} besvarade ·{' '}
          {nf(result?.averageDeclinedPerDay ?? 0, 1)} per dygn ·{' '}
          {nf(result?.fieldTestersWhoDeclined ?? 0)} olika FT
        </p>

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Period</th>
              <th style={thNumStyle}>Nekade</th>
              <th style={thNumStyle}>Accepterade</th>
              <th style={thNumStyle}>Andel nekade</th>
            </tr>
          </thead>
          <tbody>
            {(result?.totals ?? []).map((row) => (
              <tr
                key={new Date(row.date).toISOString()}
                style={{ borderBottom: '1px solid #eee' }}
              >
                <td style={tdStyle}>{periodLabel(row.date, groupingLevel)}</td>
                <td style={tdNumStyle}>{nf(row.declined)}</td>
                <td style={tdNumStyle}>{nf(row.accepted)}</td>
                <td style={tdNumStyle}>{pct(row.declinedPercentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div style={{ margin: '16px 0 8px' }}>
          <Select
            small
            value={dimension}
            onChange={(e) =>
              setDimension(
                Number(e.target.value) as DeclinedJobsCountyDimension
              )
            }
          >
            {Object.values(DeclinedJobsCountyDimension).map((d) => (
              <option key={d} value={d}>
                Per {DIMENSION_LABELS[d].toLowerCase()}
              </option>
            ))}
          </Select>
        </div>

        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>{DIMENSION_LABELS[dimension]}</th>
              <th style={thNumStyle}>Nekade</th>
              <th style={thNumStyle}>Accepterade</th>
              <th style={thNumStyle}>Andel nekade</th>
            </tr>
          </thead>
          <tbody>
            {counties.map((row) => {
              const isOpen = canExpand && expanded.isOpen(row.countyName);
              return (
                <Fragment key={row.countyName}>
                  <tr
                    style={{
                      borderBottom: '1px solid #eee',
                      cursor: canExpand ? 'pointer' : undefined,
                    }}
                    onClick={
                      canExpand
                        ? () => expanded.toggle(row.countyName)
                        : undefined
                    }
                  >
                    <td style={tdStyle}>
                      {canExpand && (isOpen ? '▾ ' : '▸ ')}
                      {row.countyName}
                    </td>
                    <td style={tdNumStyle}>{nf(row.declined)}</td>
                    <td style={tdNumStyle}>{nf(row.accepted)}</td>
                    <td style={tdNumStyle}>{pct(row.declinedPercentage)}</td>
                  </tr>
                  {isOpen &&
                    (result?.byFromCountyAndLocality ?? [])
                      .filter((sub) => sub.countyName === row.countyName)
                      .map((sub) => (
                        <tr
                          key={`${row.countyName}-${sub.locality}`}
                          style={{
                            borderBottom: '1px solid #f3f3f3',
                            color: '#555',
                          }}
                        >
                          <td style={{ ...tdStyle, paddingLeft: '28px' }}>
                            {localityLabel[sub.locality]}
                          </td>
                          <td style={tdNumStyle}>{nf(sub.declined)}</td>
                          <td style={tdNumStyle}>{nf(sub.accepted)}</td>
                          <td style={tdNumStyle}>
                            {pct(sub.declinedPercentage)}
                          </td>
                        </tr>
                      ))}
                </Fragment>
              );
            })}
            <tr style={totalRowStyle}>
              <td style={tdStyle}>Totalt</td>
              <td style={tdNumStyle}>{nf(result?.declined ?? 0)}</td>
              <td style={tdNumStyle}>{nf(result?.accepted ?? 0)}</td>
              <td style={tdNumStyle}>{pct(result?.declinedPercentage)}</td>
            </tr>
          </tbody>
        </table>

        <table style={{ ...tableStyle, marginTop: '16px' }}>
          <thead>
            <tr>
              <th style={thStyle}>Från-län mot hemankare</th>
              <th style={thNumStyle}>Nekade</th>
              <th style={thNumStyle}>Accepterade</th>
              <th style={thNumStyle}>Andel nekade</th>
            </tr>
          </thead>
          <tbody>
            {(result?.byLocality ?? []).map((row) => (
              <tr key={row.locality} style={{ borderBottom: '1px solid #eee' }}>
                <td style={tdStyle}>{localityLabel[row.locality]}</td>
                <td style={tdNumStyle}>{nf(row.declined)}</td>
                <td style={tdNumStyle}>{nf(row.accepted)}</td>
                <td style={tdNumStyle}>{pct(row.declinedPercentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </WidgetState>
    </Widget>
  );
};

export default DeclinedJobsWidget;
